import { Injectable } from "@angular/core";
import { HttpResponse } from "@angular/common/http";
import { AppConfigService } from "./app-config.service";

export interface CacheEntry {
  timestamp: number,
  response: HttpResponse<any>
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheEntry>();

  private cacheValidity = 0;

  constructor(
    private appConfigService: AppConfigService,
  ) {
    this.setCacheValidity(this.appConfigService.getCacheTime());
  }

  setCacheValidity(cacheValidity: number) {
    this.cacheValidity = cacheValidity;
  }

  public setCachedResponse(key: string, response: HttpResponse<any>): void {
    const entry: CacheEntry = {
      timestamp: Date.now(),
      response: response
    }

    this.cache.set(key, entry);
  }

  public getCachedResponse(key: string): HttpResponse<any> | undefined {
    const cachedResponse = this.cache.get(key);

    if (!cachedResponse) {
      return;
    }

    const requestTimestamp = Date.now();

    if ((requestTimestamp - cachedResponse.timestamp) > this.cacheValidity) {
      return;
    }

    return cachedResponse.response;
  }

  public clearCachedResponse(): void {
    this.cache.clear();
  }

  public isCacheableRequest(endpoint: string): boolean {
    const cacheableUrls = [
      '/api/Funcionario/PossuiAcessoNaTela',
      '/api/Empresa/BuscarPremissas',
      '/api/Empresa/TextoKitFechado',
      '/api/Empresa/ValidarKitCustomizado',
      '/api/Empresa/Cnaes',
      '/api/Empresa/TelhadoCalc',
      '/api/Empresa/FornEstruturaPadrao',
      '/api/Empresa/CadIntFisico',
      '/api/Empresa/TxRural',
      '/api/Empresa/FreteMinimo',
      '/api/Empresa/UsarTbFrete',
      '/api/Empresa/DadosObgMonitores',
      '/api/Empresa/VerPrecoProdutos',
      '/api/Empresa/VendedorPadrao',
      '/api/Empresa/MostrarVendaAvulso',
      '/api/Empresa/DescPadrao',
      '/api/Empresa/FreteAut',
      '/api/Empresa/NfeCobranca',
      '/api/Empresa/TipoCalculo',
      '/api/Empresa/TxVendaDireta',
      '/api/Empresa/TextoFidelidade',
      '/api/Empresa/UsarFidelidade',
      '/api/Empresa/DesativarPaisagem',
      '/api/Empresa/MonConferencia',
      '/api/Empresa/MonIntegracao',
      '/api/Empresa/MonTriagemPendencia',
      '/api/Empresa/MostrarCodErp',
      '/api/Empresa/ObsNoItem',
      '/api/Empresa/UsarPrecoAvulso',
      '/api/Empresa/CompConfObg',
      '/api/Empresa/NovoEndEntrega',
      '/api/Empresa/IntegradorAnexar',
      '/api/Empresa/MostrarVenContato',
      '/api/Empresa/MostrarDadosFinanceiros',
      '/api/Empresa/MostrarBifasico',
      '/api/Empresa/MultiFornecedores',
      '/api/Empresa/UsarCombImp',
      '/api/Empresa/LimiteRepasse',
      '/api/Empresa/UsarTelCeramico',
      '/api/Empresa/UsarTransformador',
      '/api/Empresa/UsarCombInvStrCon',
      '/api/Empresa/MostrarNumeroSerie',
      '/api/Empresa/AlterarValorFinal',
      '/api/Empresa/MostrarCotDolar',
      '/api/Empresa/KitProntoFrete',
      '/api/Empresa/KitProntoDesconto',
      '/api/Empresa/BuscarEstruturasPersonalizadas',
      '/api/Empresa/AdProdKitPronto',
      '/api/Empresa/ConfirmarSemValidar',
      '/api/Empresa/AtivarVoucher',
      '/api/Empresa/BoletoFotus',
      '/api/Empresa/BloqClonar',
      '/api/Empresa/BloqExcluirAnexo',
      '/api/Empresa/MostrarRecebimentosDashboard',
      '/api/Empresa/IntegVerVlServico',
      '/api/Empresa/CentroDeDistribuicao',
      '/api/Empresa/DispEstoque',
      '/api/Empresa/VendAlterarFrete',
      '/api/Empresa/UsarDescarga',
      '/api/Empresa/Questionario',
      '/api/Empresa/UsarServicoEntrega',
      '/api/Empresa/UsarSeguro',
      '/api/Empresa/UsarValorProjeto',
      '/api/Empresa/FreteManualAvulso',
      '/api/Empresa/FreteAdicionarServico',
      '/api/Empresa/FreteAdicionarSeguro',
      '/api/Empresa/UsarEstoqueMinimo',
      '/api/Empresa/UsarMultiFormaPagamento',
      '/api/Empresa/BloqPedidoSemEstoqueDisp',
      '/api/Empresa/TrocarRazaoPorNomeFantasia',
      '/api/Empresa/CodEverest',
      '/api/Empresa/NaoRespTimePesqCliente',
      '/api/Empresa/InscricaoEstadualObg',
      '/api/Empresa/MostrarDescKit',
      '/api/Empresa/QtdPainelPorFileira',
      '/api/Empresa/MonConferenciaTecnica',
      '/api/Empresa/MonVerificacao',
      '/api/Empresa/MonAnaliseFinanceira',
      '/api/Empresa/MonFaturamento',
      '/api/Empresa/UsarAcrescimo',
      '/api/Empresa/OcultarColunaDescricaoFileiras',
      '/api/Empresa/PedirCodErpIntegracao',
      '/api/Empresa/InformarDadosSeparacao',
      '/api/Empresa/NomeContratoParceria',
      '/api/Empresa/UsarDocuSign',
      '/api/Empresa/MarketPlace',
      '/api/Empresa/MostrarPesquisaGeral',
      '/api/Empresa/VendaProgramada',
      '/api/Empresa/VenDiNaoAltEndPJ',
      '/api/Empresa/IntPodeAlterarCD',
      '/api/Empresa/IntegracaoSolaryum',
      '/api/Empresa/MostrarMsgTrocaCD',
      '/api/Empresa/EstoqueVirtual',
      '/api/Empresa/AdicionarVariosKitPronto',
      '/api/Empresa/BuscarModulosAtivos',
      '/api/Empresa/UsarFormulaRepasseCif',
      '/api/Empresa/DescClienteSobreporVendedor',
      '/api/Empresa/MsgEnderecoAdicional',
      '/api/Empresa/CalcCondPagNoFinal',
      '/api/Empresa/PremissasOrcPed',
      '/api/Empresa/CalcValorFinalSugestao',
      '/api/Empresa/RemoverItemSugEdicao',
      '/api/Empresa/UsarReservaManual',
      '/api/Empresa/DadosSeparacaoObg',
      '/api/Empresa/VisualizacaoKitFechado',
      '/api/Empresa/CRMVisualizarValor',
      '/api/Empresa/CRMEditarAtividade',
      '/api/Empresa/ConfirmarMudancaEtapa',
      '/api/Empresa/PergAlterarAtividade',
      '/api/Empresa/IntegradorPodeAbrirOcorrencia',
      '/api/Empresa/EstoqueUnicoLote',
      '/api/Empresa/ValorArredondamento',
      '/api/Empresa/UsarCRM',
      '/api/Empresa/UsarLojaProdutos',
      '/api/Empresa/IntegracaoPlataforma',
      '/api/Empresa/BloquearSistManutencaoProdutos',
      '/api/Empresa/ObrigaSelecionarInversorMontarKit',
    ];

    return cacheableUrls.some(url => endpoint.includes(url));
  }
}
